import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { CapacidadService } from '../../core/services/capacidad.service';
import { AlertService } from '../../core/services/alert.service';
import { CapacidadApi, RangoHorarioApi } from '../../core/models/api/empresa-api';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';

@Component({
    selector: 'app-admin-capacidad',
    standalone: true,
    imports: [CommonModule, FormsModule, UiLoadingComponent],
    template: `
    <div class="mb-6">
      <h2 class="text-2xl font-black text-gray-800">Cupos de entrega</h2>
      <p class="text-sm text-gray-400 mt-1">Define cuántos pedidos se pueden entregar por franja. Elige un día para ajustar sus cupos; las franjas por defecto aplican a los días nuevos.</p>
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <ng-container *ngIf="!loading()">
      <section class="bg-white rounded-[2rem] p-5 border border-rose-100 shadow-sm mb-6">
        <button type="button" (click)="mostrarFranjas.set(!mostrarFranjas())" class="w-full flex items-center justify-between gap-2 cursor-pointer">
          <span class="text-sm font-black text-gray-800">Franjas y cupos por defecto</span>
          <span class="text-xs font-bold text-rose-500">{{ mostrarFranjas() ? 'Ocultar' : 'Editar' }}</span>
        </button>
        <p class="text-xs text-gray-400 mt-1">"Se puede pedir hasta" es la hora límite para pedir esa franja.</p>
        <div *ngIf="mostrarFranjas()" class="mt-4 flex flex-col gap-4">
          <div *ngFor="let r of rangos(); let i = index" class="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <label class="md:col-span-3 block">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre</span>
              <input [ngModel]="r.nombre" (ngModelChange)="patchRango(i, { nombre: $event })" [name]="'nom'+i" class="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm" />
            </label>
            <label class="md:col-span-2 block">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Inicio</span>
              <input [ngModel]="toInput(r.horaInicio)" (ngModelChange)="patchRango(i, { horaInicio: fromInput($event) })" [name]="'ini'+i" type="time" class="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm" />
            </label>
            <label class="md:col-span-2 block">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Fin</span>
              <input [ngModel]="toInput(r.horaFin)" (ngModelChange)="patchRango(i, { horaFin: fromInput($event) })" [name]="'fin'+i" type="time" class="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm" />
            </label>
            <label class="md:col-span-2 block">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Se puede pedir hasta</span>
              <input [ngModel]="toInput(r.horaCierrePedidos)" (ngModelChange)="patchRango(i, { horaCierrePedidos: fromInput($event) })" [name]="'cie'+i" type="time" class="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm" />
            </label>
            <label class="md:col-span-2 block">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Cupos por defecto</span>
              <input [ngModel]="r.capacidadDefault" (ngModelChange)="patchRango(i, { capacidadDefault: +$event })" [name]="'cap'+i" type="number" min="0" class="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm" />
            </label>
          </div>
          <p *ngIf="rangos().length === 0" class="text-sm text-gray-400 text-center py-4">No hay franjas configuradas.</p>
          <div class="flex justify-end">
            <button type="button" (click)="guardarRangos()" [disabled]="guardandoFranjas() || rangos().length === 0" class="px-6 py-3 rounded-2xl text-white text-sm font-bold disabled:opacity-40 cursor-pointer" style="background-color: var(--bg-main);">
              {{ guardandoFranjas() ? 'Guardando…' : 'Guardar franjas' }}
            </button>
          </div>
        </div>
      </section>

      <section class="bg-white rounded-[2rem] p-5 border border-rose-100 shadow-sm">
        <p class="text-sm font-black text-gray-800">Cupos por día</p>
        <p class="text-xs text-gray-400 mt-1">Elige un día y ajusta sus cupos. Los días en rojo ya tienen franjas llenas.</p>

        <div class="mt-4 flex gap-2 overflow-x-auto pb-2">
          <button *ngFor="let d of dias()" type="button" (click)="diaSel.set(d.fecha)"
            class="shrink-0 w-20 py-2.5 rounded-2xl border-2 flex flex-col items-center gap-0.5 cursor-pointer transition-colors"
            [class.border-rose-500]="diaSel() === d.fecha"
            [class.bg-rose-50]="diaSel() === d.fecha"
            [class.border-gray-100]="diaSel() !== d.fecha">
            <span class="text-[10px] font-bold uppercase text-gray-400">{{ d.etiqueta }}</span>
            <span class="text-base font-black text-gray-800">{{ d.dia }}</span>
            <span class="text-[10px] font-bold" [class.text-red-500]="d.llenos > 0" [class.text-gray-400]="d.llenos === 0">{{ d.llenos > 0 ? d.llenos + ' lleno(s)' : '—' }}</span>
          </button>
        </div>

        <div class="mt-4 flex flex-col gap-3">
          <p *ngIf="capacidadesDelDia().length === 0" class="text-sm text-gray-400 text-center py-4">No hay franjas para este día.</p>
          <div *ngFor="let c of capacidadesDelDia()" class="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center rounded-2xl border border-gray-100 px-4 py-3">
            <div class="sm:col-span-5">
              <p class="text-sm font-bold text-gray-800">{{ c.rangoHorario }}</p>
            </div>
            <div class="sm:col-span-3">
              <p class="text-[11px] font-bold uppercase text-gray-400">Usados</p>
              <p class="font-black text-gray-800">{{ usados(c) }}</p>
            </div>
            <div class="sm:col-span-4">
              <p class="text-[11px] font-bold uppercase text-gray-400">Cupo</p>
              <input [ngModel]="c.capacidadMaxima" (ngModelChange)="patchCap(c.uuid, +$event)" [name]="'max'+c.uuid" type="number" min="0"
                [class.border-red-400]="c.capacidadMaxima < usados(c)"
                class="w-28 rounded-xl border border-gray-200 px-3 py-1.5 text-sm" />
              <span *ngIf="c.capacidadMaxima < usados(c)" class="text-[11px] text-red-500 block">Menor a lo ya usado</span>
            </div>
          </div>
          <div class="flex justify-end" *ngIf="capacidadesDelDia().length > 0">
            <button type="button" (click)="guardarDia()" [disabled]="guardandoDia()" class="px-6 py-3 rounded-2xl text-white text-sm font-bold disabled:opacity-40 cursor-pointer" style="background-color: var(--bg-main);">
              {{ guardandoDia() ? 'Guardando…' : 'Guardar cambios del día' }}
            </button>
          </div>
        </div>
      </section>
    </ng-container>
    `
})
export class AdminCapacidadComponent implements OnInit {
    private capacidadService = inject(CapacidadService);
    private alerts = inject(AlertService);

    loading = signal(true);
    rangos = signal<RangoHorarioApi[]>([]);
    capacidades = signal<CapacidadApi[]>([]);
    mostrarFranjas = signal(false);
    diaSel = signal('');
    guardandoFranjas = signal(false);
    guardandoDia = signal(false);

    dias = computed(() => {
        const fechas = Array.from(new Set(this.capacidades().map((c) => this.fecha(c)))).sort();
        return fechas.map((fecha) => {
            const date = new Date(`${fecha}T00:00:00`);
            const llenos = this.capacidades().filter((c) => this.fecha(c) === fecha && c.capacidadMaxima <= this.usados(c)).length;
            return {
                fecha,
                etiqueta: date.toLocaleDateString('es-PE', { weekday: 'short' }),
                dia: date.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }),
                llenos
            };
        });
    });

    capacidadesDelDia = computed(() => this.capacidades().filter((c) => this.fecha(c) === this.diaSel()));

    ngOnInit(): void {
        const desde = this.iso(new Date());
        const hastaDate = new Date();
        hastaDate.setDate(hastaDate.getDate() + 13);
        const hasta = this.iso(hastaDate);

        this.capacidadService.listRangosHorario().subscribe({
            next: (rangos) => {
                this.rangos.set(rangos);
                this.capacidadService.listCapacidades(desde, hasta).subscribe({
                    next: (rows) => {
                        this.capacidades.set(rows);
                        this.diaSel.set(this.dias()[0]?.fecha ?? desde);
                        this.loading.set(false);
                    },
                    error: () => {
                        this.loading.set(false);
                        this.alerts.error('No se pudieron cargar los cupos.');
                    }
                });
            },
            error: () => {
                this.loading.set(false);
                this.alerts.error('No se pudieron cargar las franjas.');
            }
        });
    }

    toInput(value: string) {
        return value ? value.slice(0, 5) : '';
    }

    fromInput(value: string) {
        return value ? (value.length === 5 ? `${value}:00` : value) : '00:00:00';
    }

    usados(c: CapacidadApi): number {
        return c.cantidadReservada + c.cantidadConfirmada;
    }

    patchRango(index: number, partial: Partial<RangoHorarioApi>) {
        this.rangos.update((list) => list.map((item, i) => i === index ? { ...item, ...partial } : item));
    }

    patchCap(uuid: string, value: number) {
        this.capacidades.update((list) => list.map((item) => item.uuid === uuid ? { ...item, capacidadMaxima: value } : item));
    }

    guardarRangos() {
        if (this.guardandoFranjas() || this.rangos().length === 0) {
            return;
        }
        this.guardandoFranjas.set(true);
        forkJoin(this.rangos().map((r) => this.capacidadService.updateRangoHorario(r.uuid, {
            nombre: r.nombre,
            horaInicio: r.horaInicio,
            horaFin: r.horaFin,
            horaCierrePedidos: r.horaCierrePedidos,
            capacidadDefault: Number(r.capacidadDefault) || 0,
            orden: r.orden
        }))).subscribe({
            next: (resultados) => {
                this.guardandoFranjas.set(false);
                resultados.every((ok) => ok) ? this.alerts.ok('Franjas guardadas') : this.alerts.error('Algunas franjas no se pudieron guardar');
            },
            error: () => {
                this.guardandoFranjas.set(false);
                this.alerts.error('No se pudieron guardar las franjas');
            }
        });
    }

    guardarDia() {
        if (this.guardandoDia()) {
            return;
        }
        const filas = this.capacidadesDelDia();
        if (filas.some((c) => c.capacidadMaxima < this.usados(c))) {
            this.alerts.error('Hay cupos menores a lo ya usado. Corrígelos antes de guardar.');
            return;
        }
        if (filas.length === 0) {
            return;
        }
        this.guardandoDia.set(true);
        forkJoin(filas.map((c) => this.capacidadService.updateCapacidad({
            rangoHorarioUuid: c.rangoHorarioUuid,
            fecha: this.fecha(c),
            capacidadMaxima: Number(c.capacidadMaxima) || 0
        }))).subscribe({
            next: (resultados) => {
                this.guardandoDia.set(false);
                resultados.every((ok) => ok) ? this.alerts.ok('Cupos del día actualizados') : this.alerts.error('Algunos cupos no se pudieron actualizar');
            },
            error: () => {
                this.guardandoDia.set(false);
                this.alerts.error('No se pudieron actualizar los cupos');
            }
        });
    }

    private fecha(c: CapacidadApi): string {
        return c.fecha.slice(0, 10);
    }

    private iso(date: Date) {
        return date.toISOString().slice(0, 10);
    }
}
